import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import type { ReactNode } from "react";
import { BotIcon } from "lucide-react";

import { ClaudeAI, CursorIcon, OpenAI } from "~/components/Icons";
import { taskAssigneeName } from "../taskPresentation";
import { TaskAssigneeIdentity } from "./TaskAssigneeIdentity";

type ActiveAgent = CmmdBoardTask["activeAgents"][number];

const VISIBLE_AGENT_LIMIT = 2;

const PROVIDER_LABEL_BY_KEY: Readonly<Record<string, string>> = {
  openai: "Codex",
  codex: "Codex",
  "openai-codex": "Codex",
  anthropic: "Claude",
  claude: "Claude",
  "anthropic-claude": "Claude",
  cursor: "Cursor",
  forge: "Forge",
};

const PROVIDER_LABEL_BY_KIND: Readonly<Record<ActiveAgent["agentKind"], string>> = {
  codex: "Codex",
  claude: "Claude",
  cursor: "Cursor",
  forge: "Forge",
  integration: "Integration",
  custom: "Agent",
};

function agentProviderLabel(agent: ActiveAgent): string {
  const provider = agent.providerKey?.trim().toLowerCase();
  return (
    (provider === undefined ? undefined : PROVIDER_LABEL_BY_KEY[provider]) ??
    PROVIDER_LABEL_BY_KIND[agent.agentKind]
  );
}

/**
 * Provider marks on a card face, all at one weight.
 *
 * `OpenAI` and `CursorIcon` already ship as `fill-black dark:fill-white`, so two of
 * the three were neutral here and `ClaudeAI` alone carried its brand `#d97757` —
 * an orange that also sits right next to Forge's `--primary`. That made the same
 * cluster render at two different weights depending on which agent picked the task
 * up. `fill-current` puts Claude on the same footing as its neighbours; the mark's
 * silhouette is what identifies the provider at 12px, not its hue.
 *
 * Scoped to the card/row cluster on purpose. Pickers, settings, and the model list
 * still render every provider in full brand color, where the mark is the subject
 * rather than a repeated line-item glyph.
 */
function AgentProviderMark({ agent }: { readonly agent: ActiveAgent }) {
  const provider = agentProviderLabel(agent);
  if (provider === "Codex") return <OpenAI className="size-3" aria-hidden />;
  if (provider === "Claude") return <ClaudeAI className="size-3 fill-current" aria-hidden />;
  if (provider === "Cursor") return <CursorIcon className="size-3" aria-hidden />;
  if (provider === "Forge") {
    return <img alt="" aria-hidden className="size-3 object-contain" src="/forge-mark.svg" />;
  }
  return <BotIcon className="size-3" aria-hidden />;
}

function AgentProviderBadge({ agent }: { readonly agent: ActiveAgent }) {
  const provider = agentProviderLabel(agent);
  const label = `Working agent: ${agent.displayName}, ${provider}`;
  return (
    <span
      aria-label={label}
      title={`${agent.displayName} · ${provider}`}
      className="inline-flex size-5 items-center justify-center rounded-full text-foreground ring-1 ring-inset ring-border"
    >
      <AgentProviderMark agent={agent} />
    </span>
  );
}

/**
 * Who is *working* a task: the assigned subject, then the agents actively on it.
 *
 * Despite the name this renders `assignment` / `assignee`, NOT `task.ownerId` —
 * owner is a separate, accountability-shaped field that the list view surfaces
 * as its own column. The two are deliberately not merged: an agent is routinely
 * an assignee and never an owner.
 *
 * Shared by the board card, the list row, and the mobile row so all three speak
 * one visual language — a neutral ring on every seat, with identity carried by what
 * sits inside it: the person's avatar, or the provider's own mark. Forge does not add
 * a hue of its own here. The rings were amber (assignee) and blue (working agent),
 * which put two colors on the one cluster present on every single card, and the amber
 * was close enough to `--primary` to read as the brand accent.
 */
export function TaskCardOwnership({ task }: { readonly task: CmmdBoardTask }) {
  const visibleAgents = task.activeAgents.slice(0, VISIBLE_AGENT_LIMIT);
  const overflowAgents = task.activeAgents.slice(VISIBLE_AGENT_LIMIT);
  const overflowTitle = overflowAgents
    .map((agent) => `${agent.displayName} · ${agentProviderLabel(agent)}`)
    .join("\n");
  const assignment = task.assignment;
  const isAgentOwner = assignment?.kind === "agent_member";
  const ownerName = taskAssigneeName(task);
  const ownerKind = isAgentOwner ? "agent" : ownerName === null ? null : "human";

  return (
    <span data-task-card-ownership className="inline-flex min-w-0 items-center gap-1">
      <span
        aria-label="Task assignee"
        className="inline-flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full ring-1 ring-border"
      >
        <TaskAssigneeIdentity
          name={ownerName}
          avatarUrl={isAgentOwner ? null : task.assigneeAvatar}
          kind={ownerKind}
          {...(isAgentOwner ? { agentId: assignment.agentId } : {})}
          showUnassigned
        />
      </span>
      {task.activeAgents.length > 0 ? (
        <>
          <span aria-label="Active agents" className="inline-flex gap-1">
            {visibleAgents.map((agent) => (
              <AgentProviderBadge key={agent.agentMemberId} agent={agent} />
            ))}
          </span>
          {overflowAgents.length > 0 ? (
            <span
              aria-label={`${overflowAgents.length} more working agents`}
              title={overflowTitle}
              className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-medium text-muted-foreground ring-1 ring-inset ring-border"
            >
              +{overflowAgents.length}
            </span>
          ) : null}
        </>
      ) : null}
    </span>
  );
}

/**
 * {@link TaskCardOwnership} for surfaces that must render *something else* when
 * nobody is on the task.
 *
 * The board card always shows an explicit "Unassigned" chip, so it uses the
 * cluster directly. Rows have a per-surface empty state — an em dash in a table
 * cell, nothing at all on a phone — and owning that choice here keeps the
 * branch out of the row components.
 */
export function TaskAssigneeCluster({
  task,
  fallback = null,
}: {
  readonly task: CmmdBoardTask;
  readonly fallback?: ReactNode;
}) {
  const hasAnyone = taskAssigneeName(task) !== null || task.activeAgents.length > 0;
  return hasAnyone ? <TaskCardOwnership task={task} /> : <>{fallback}</>;
}
